import { normalizeVoiceText, parseVoiceDateKey } from "../../voice/ownerVoiceParsers";
import { chooseEntity, spokenDate } from "../ownerJarvisUtils";

function navigationQuery(value = "") {
  return normalizeVoiceText(value)
    .replace(/\b(?:jarvis|abr\w*|mostr\w*|mir\w*|and\w*|llev\w*|ver|ir|quiero|quisiera|la|el|una|un|ficha|detalle|de|del|al|a|cliente|clientes|persona|personas|proyecto|proyectos|frente|iniciativa|rutina|rutinas|habito|habitos|dia|fecha)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function clientMutationRequest(text = "") {
  return /\b(?:crear|crea|creame|crearme|creando|nuevo|nueva|cargar|carga|cargame|cargarme|registrar|registra|registrame|agregar|agrega|agregame|sumar|suma|sumame|anotar|anota|anotame|editar|edita|actualizar|actualiza|modificar|modifica|borrar|borra|eliminar|elimina)\b/.test(text);
}

function shortClientOpenRequest(text = "", query = "") {
  if (!/\b(?:cliente|persona)\b/.test(text) || !query || clientMutationRequest(text)) return false;
  return query.split(" ").some((word) => word.length >= 2);
}

function projectNoteRequest(text = "") {
  return /\b(?:anot\w*|apunt\w*|agreg\w*|carg\w*|registr\w*|dej\w*)\b/.test(text)
    && /\b(?:nota|algo|anotacion|apunte|proyecto|frente|iniciativa)\b/.test(text)
    && /\b(?:proyecto|frente|iniciativa)\b/.test(text);
}

function projectNoteTitle(raw = "") {
  const clean = String(raw || "").trim();
  const match = clean.match(/\bque\s+(.+)$/i) || clean.match(/:\s*(.+)$/);
  const candidate = String(match?.[1] || "").trim();
  return candidate.length >= 3 && candidate.length <= 180 ? candidate : "";
}

function contractCreationRequest(text = "") {
  return /\b(?:crear|crea|armar|arma|definir|define|nuevo|nueva|hacer)\b/.test(text)
    && /\b(?:contrato|acuerdo base)\b/.test(text);
}

function currentProjectId() {
  if (typeof window === "undefined") return "";
  const match = String(window.location.pathname || "").match(/^\/dashboard\/owner\/proyectos\/([^/?#]+)/);
  if (!match?.[1]) return "";
  try { return decodeURIComponent(match[1]); } catch { return match[1]; }
}

function contractProjectQuery(raw = "") {
  return normalizeVoiceText(raw)
    .replace(/\b(?:jarvis|crear|crea|armar|arma|definir|define|nuevo|nueva|hacer|quiero|un|una|el|la|contrato|acuerdo|base|para|de|del|proyecto|frente|iniciativa)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

async function openProjectContract({ ownerApi, navigate, initiativeId, initiativeName = "" }) {
  const response = await ownerApi.projectAgreement(initiativeId);
  const agreement = response?.agreement || null;
  const active = (agreement?.terms || []).filter((term) => term.status !== "cancelled");
  const base = active.find((term) => term.kind === "base" && term.billingMode !== "monthly" && term.billingSource !== "webprofit") || null;
  const anyBase = active.find((term) => term.kind === "base") || null;
  const projectName = agreement?.initiative?.name || initiativeName || "este proyecto";

  if (base) {
    navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`, {
      state: { ownerJarvisUi: { surface: "project360", initiativeId, section: "project-contract", form: "contract_edit", termId: String(base.id || base._id || ""), token: `contract-edit:${initiativeId}:${Date.now()}` } },
    });
    return { handled: true, message: `${projectName} ya tiene contrato base. Te lo abro para que lo revises o edites.` };
  }

  if (anyBase?.billingMode === "monthly") {
    navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`, {
      state: { ownerJarvisUi: { surface: "project360", initiativeId, section: "project-billing", form: "monthly_edit", termId: String(anyBase.id || anyBase._id || ""), token: `monthly-edit:${initiativeId}:${Date.now()}` } },
    });
    return { handled: true, message: `${projectName} ya tiene un acuerdo base mensual. No voy a crear otro contrato base encima; te abro ese acuerdo para que decidas si corresponde editarlo.` };
  }

  navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`, {
    state: { ownerJarvisUi: { surface: "project360", initiativeId, section: "project-contract", form: "contract_new", token: `contract-new:${initiativeId}:${Date.now()}` } },
  });
  return { handled: true, message: `Te abro directamente el contrato base de ${projectName}.` };
}

async function resolveProject(ownerApi, text) {
  const initiativesResponse = await ownerApi.listInitiatives({ status: "", limit: 120 });
  return chooseEntity(initiativesResponse?.initiatives || [], text, ["name", "outcome", "notes"]);
}

export function createOwnerJarvisEntityNavigationCapability({ ownerApi, navigate }) {
  return {
    id: "entity-navigation",
    domain: "navigation",
    async handle({ raw, text }) {
      const wantsClient = /\b(cliente|clientes|persona|personas)\b/.test(text);
      const wantsProject = /\b(proyecto|frente|iniciativa)\b/.test(text);
      const wantsRoutine = /\b(rutina|habito)\b/.test(text);

      if (contractCreationRequest(text)) {
        let initiativeId = currentProjectId();
        let initiativeName = "";
        if (!initiativeId) {
          const query = contractProjectQuery(raw);
          if (!query) return { handled: true, message: "Decime de qué proyecto querés crear el contrato." };
          const result = await resolveProject(ownerApi, query);
          if (!result.row) {
            if (result.alternatives?.length) return { handled: true, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row) => row.name).join(", ")}. Decime cuál.` };
            return { handled: true, message: "No encontré ese proyecto. Decime el nombre como aparece en Proyectos." };
          }
          initiativeId = String(result.row?.id || result.row?._id || "");
          initiativeName = result.row?.name || "";
        }
        return openProjectContract({ ownerApi, navigate, initiativeId, initiativeName });
      }

      if (projectNoteRequest(text)) {
        const result = await resolveProject(ownerApi, text);
        if (result.row) {
          const initiativeId = String(result.row?.id || result.row?._id || "");
          const title = projectNoteTitle(raw);
          navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`, {
            state: {
              ownerJarvisUi: {
                surface: "project360",
                initiativeId,
                section: "project-work",
                form: "task_new",
                focusField: "title",
                preset: { projectItemType: "note", ...(title ? { title } : {}) },
                token: `project-note:${initiativeId}:${Date.now()}`,
              },
            },
          });
          return { handled: true, message: `Abro una nota nueva directamente en ${result.row.name}.` };
        }
        if (result.alternatives?.length) return { handled: true, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row) => row.name).join(", ")}. Decime cuál.` };
        return { handled: true, message: "No encontré ese proyecto. Decime el nombre como aparece en Proyectos." };
      }

      const navigationRequest = /\b(?:abr\w*|mostr\w*|mir\w*|llev\w*|and\w*|ir|ver)\b/.test(text);
      const query = navigationQuery(text);
      const shortClientRequest = shortClientOpenRequest(text, query);
      if (!navigationRequest && !shortClientRequest) return null;
      if (wantsClient && navigationRequest && !query) {
        navigate("/dashboard/owner/clientes");
        return { handled: true, message: "Abro Clientes." };
      }
      const dateKey = !wantsClient && !wantsProject && !wantsRoutine ? parseVoiceDateKey(raw) : null;
      if (dateKey && /\b(?:dia|fecha|hoy|ayer|lunes|martes|miercoles|jueves|viernes|sabado|domingo|enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre|\d{1,2})\b/.test(text)) {
        navigate(`/dashboard/owner/calendario?fecha=${encodeURIComponent(dateKey)}`);
        return { handled: true, message: `Abro el ${spokenDate(dateKey)}.` };
      }

      // Si el usuario ya dijo qué entidad quiere, no consultamos los otros dominios.
      // Para personas traemos candidatos reales sin el filtro textual del endpoint:
      // chooseEntity ya hace el matching tolerante y conserva la ambigüedad segura.
      const [clientsResponse, initiativesResponse, routinesResponse] = await Promise.all([
        wantsClient || (!wantsProject && !wantsRoutine) ? ownerApi.listClients({ limit: 500 }) : Promise.resolve({ clients: [] }),
        wantsProject || (!wantsClient && !wantsRoutine) ? ownerApi.listInitiatives({ status: "", limit: 100 }) : Promise.resolve({ initiatives: [] }),
        wantsRoutine || (!wantsClient && !wantsProject) ? ownerApi.listRoutines({ includeInactive: true, limit: 150 }) : Promise.resolve({ routines: [] }),
      ]);
      let candidates = [
        ...(clientsResponse?.clients || []).map((row) => ({ ...row, __kind: "client" })),
        ...(initiativesResponse?.initiatives || []).map((row) => ({ ...row, __kind: "project" })),
        ...(routinesResponse?.routines || []).map((row) => ({ ...row, __kind: "routine" })),
      ];
      if (wantsClient) candidates = candidates.filter((row) => row.__kind === "client");
      if (wantsProject) candidates = candidates.filter((row) => row.__kind === "project");
      if (wantsRoutine) candidates = candidates.filter((row) => row.__kind === "routine");
      const result = chooseEntity(candidates, query, ["name", "company", "email", "outcome", "notes"]);
      if (result.row) {
        const row = result.row;
        const id = String(row?.id || row?._id || "");
        if (row.__kind === "client") navigate(`/dashboard/owner/clientes?cliente=${encodeURIComponent(id)}`);
        if (row.__kind === "project") navigate(`/dashboard/owner/proyectos/${encodeURIComponent(id)}`);
        if (row.__kind === "routine") navigate(`/dashboard/owner/rutinas/${encodeURIComponent(id)}`);
        return { handled: true, message: `Abro ${row.name || row.company || "la ficha"}.` };
      }
      if (result.alternatives?.length) {
        return { handled: true, message: `Encontré varias fichas parecidas: ${result.alternatives.map((row) => row.name || row.company).join(", ")}. Decime cuál.` };
      }
      return { handled: true, message: "No encontré esa ficha. Decime el nombre un poco más completo." };
    },
  };
}
